import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

import "./PrimaryPillTabs.css";

const PrimaryPillTabs = ({ tabs = [], activeTab, onChange }) => {
  const [openMenuId, setOpenMenuId] = useState(null);

  const tabsRef = useRef(null);
  const menuRefs = useRef({});

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (tabsRef.current && !tabsRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      const currentMenuId = openMenuId;

      setOpenMenuId(null);

      if (currentMenuId) {
        menuRefs.current[currentMenuId]?.focus();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  useEffect(() => {
    setOpenMenuId(null);
  }, [activeTab]);

  const handleTabClick = (tab) => {
    const hasMenu = Array.isArray(tab.menu) && tab.menu.length > 0;

    if (hasMenu) {
      setOpenMenuId((current) => (current === tab.id ? null : tab.id));
      return;
    }

    setOpenMenuId(null);
    onChange?.(tab);
  };

  const handleMenuItemClick = (item) => {
    setOpenMenuId(null);
    onChange?.(item);
  };

  return (
    <nav
      ref={tabsRef}
      className="primary-pill-tabs"
      aria-label="Primary section tabs"
    >
      {tabs.map((tab) => {
        const hasMenu = Array.isArray(tab.menu) && tab.menu.length > 0;

        const isMenuActive =
          hasMenu && tab.menu.some((item) => item.id === activeTab);

        const isActive = activeTab === tab.id || isMenuActive;

        const isOpen = openMenuId === tab.id;

        return (
          <div key={tab.id} className="primary-pill-tab-wrapper">
            <button
              ref={
                hasMenu
                  ? (node) => {
                      if (node) {
                        menuRefs.current[tab.id] = node;
                      } else {
                        delete menuRefs.current[tab.id];
                      }
                    }
                  : undefined
              }
              type="button"
              className={`primary-pill-tab ${
                isActive ? "primary-pill-tab--active" : ""
              }`}
              onClick={() => handleTabClick(tab)}
              aria-current={isActive && !hasMenu ? "page" : undefined}
              aria-haspopup={hasMenu ? "menu" : undefined}
              aria-expanded={hasMenu ? isOpen : undefined}
            >
              {tab.icon && (
                <span className="primary-pill-tab__icon">{tab.icon}</span>
              )}

              <span className="primary-pill-tab__label">{tab.label}</span>

              {hasMenu && (
                <ChevronDown
                  size={15}
                  aria-hidden="true"
                  className={`primary-pill-tab__chevron ${
                    isOpen ? "primary-pill-tab__chevron--open" : ""
                  }`}
                />
              )}
            </button>

            {hasMenu && isOpen && (
              <div
                className="primary-pill-tab__dropdown"
                role="menu"
                aria-label={`${tab.label} navigation`}
              >
                {tab.menu.map((item) => {
                  const isItemActive = activeTab === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      className={`primary-pill-tab__dropdown-item ${
                        isItemActive
                          ? "primary-pill-tab__dropdown-item--active"
                          : ""
                      }`}
                      onClick={() => handleMenuItemClick(item)}
                      aria-current={isItemActive ? "page" : undefined}
                    >
                      {item.icon && (
                        <span className="primary-pill-tab__dropdown-icon">
                          {item.icon}
                        </span>
                      )}

                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default PrimaryPillTabs;
